import { ZoneCreationProperties } from '../types/zoneMapType';
import { validateExclusiveFixtureOwnership } from './validateExclusiveFixtureOwnership';

/**
 * Single source of truth for GFC Drop Zone publish-readiness validation.
 *
 * Both the edit-time gate (useCreateZoneState.validateZoneMap) and the
 * publish-time gate (usePublishZoneMap.onPublishButton) call this so the two
 * layers can never disagree on what makes a Drop Zone map publishable.
 *
 * Checks, in order:
 *  1. every card owns at least one fixture
 *  2. every card has a category assigned
 *  3. no category is assigned to more than one card
 *  4. no fixture belongs to more than one card
 */
export function validateDropZones(zoneCreationProperties: Map<number, ZoneCreationProperties>): string[] {
  const errors: string[] = [];
  const zoneLabel = (zone: ZoneCreationProperties): string => zone.zoneName || `Zone ${zone.zoneIdx + 1}`;

  const categoryToZones = new Map<string, string[]>();
  for (const zone of zoneCreationProperties.values()) {
    const label = zoneLabel(zone);
    if (zone.fixtureIds.length === 0) {
      errors.push(`Drop Zone "${label}" must have at least one fixture`);
    }
    if (!zone.categoryId) {
      errors.push(`Drop Zone "${label}" is missing a category assignment`);
    } else {
      const labels = categoryToZones.get(zone.categoryId) ?? [];
      labels.push(label);
      categoryToZones.set(zone.categoryId, labels);
    }
  }

  for (const labels of categoryToZones.values()) {
    if (labels.length > 1) {
      errors.push(`Same category assigned to multiple Drop Zones: ${labels.join(', ')}`);
    }
  }

  const ownership = validateExclusiveFixtureOwnership(zoneCreationProperties);
  if (!ownership.isValid) {
    ownership.duplicates.forEach((zones, fixtureId) => {
      errors.push(`Fixture ${fixtureId} is assigned to multiple DropZones: ${zones.join(', ')}`);
    });
  }

  return errors;
}
